import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  List,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import colors from "../../theme/Colours";
import { orderSummaryStages } from "../../constants/orderSummary";

type StatusPoint = { status: string; date?: string; time?: string };

interface TrackDetailsProps {
  statusHistory: StatusPoint[];
  currentStage: number;
  disabled?: boolean;
}

export default function TrackDetails({
  statusHistory,
  currentStage,
  disabled = false,
}: TrackDetailsProps) {
  const byStatus = new Map(statusHistory.map((s) => [s.status, s]));

  return (
    <Accordion
      defaultExpanded={!disabled}
      disabled={disabled}
      sx={{
        mt: 2,
        boxShadow: "none",
        borderTop: `1px solid ${colors.greyBorder}`,
        ...(disabled && {
          opacity: 0.6,
          pointerEvents: "none",
        }),
      }}
    >
      <AccordionSummary
        expandIcon={!disabled && <ExpandMoreIcon />}
        sx={{
          px: 0,
          "& .MuiAccordionSummary-content": { my: 0 },
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Track details
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 0 }}>
        <List disablePadding>
          {orderSummaryStages.map((stage, idx) => {
            const relative_pos = idx - currentStage;
            const hit = byStatus.get(stage.title);

            const display = stage.customerLabel ?? stage.title;

            return (
              <Box
                key={stage.title}
                component="li"
                sx={{
                  listStyle: "none",
                  px: 2,
                  py: 1.25,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  bgcolor: relative_pos === 0 ? "action.hover" : "transparent",
                  color: relative_pos > 0 ? "text.disabled" : "inherit",
                  ...(disabled && {
                    opacity: 0.7,
                  }),
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    minWidth: 0,
                  }}
                >
                  <CheckIcon
                    sx={{
                      fontSize: 22,
                      color:
                        relative_pos <= 0
                          ? colors.primaryButtonOrange
                          : "text.disabled",
                    }}
                  />
                  <Typography
                    variant="body1"
                    noWrap
                    sx={{
                      fontWeight: relative_pos <= 0 ? 600 : 400,
                      fontSize: 14,
                    }}
                    title={display}
                  >
                    {display}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color:
                      relative_pos > 0
                        ? "text.disabled"
                        : colors.greyTypography,
                    whiteSpace: "nowrap",
                    ml: 2,
                  }}
                >
                  {hit?.date && hit?.time
                    ? `${hit.date} - ${hit.time}`
                    : "— — —"}
                </Typography>
              </Box>
            );
          })}
        </List>
      </AccordionDetails>
    </Accordion>
  );
}
